import React, { useState } from 'react';
import { Device } from '../../lib/types';
import { isFosterOnRoster, type FosterHome } from '../../lib/fosterAdapters';
import Modal, { ModalTitle, ModalSubtitle, ModalActions, ModalActionsSecondary } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import NoticeIcon from '../../components/NoticeIcon/NoticeIcon';
import ScheduleStep from '../../components/ScheduleStep/ScheduleStep';
import { combineDateTime, scheduleValidationError, type ScheduledFor } from '../../lib/scheduling';
import styles from './Devices.module.scss';

interface AdoptFromFosterModalProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  /** The saved roster, to tell whether this foster's home survives the recall. */
  homes: FosterHome[];
  onConfirm: (device: Device) => void | Promise<void>;
  onSchedule?: (device: Device, when: ScheduledFor) => Promise<void>;
}

/**
 * Confirms adopting a fostered pet into the home already fostering it — a "foster
 * fail" — with the current foster household as the adopter. Recalling the foster and
 * creating the adoption transfer both happen on confirm (see `handleAdoptFromFoster`).
 *
 * The adopter's email/phone are shown for review because this flow reuses the
 * foster's stored contact info without the editable form the manual adopter
 * transfer has — this is the rescue's chance to catch a stale address.
 */
export default function AdoptFromFosterModal({
  open,
  onClose,
  device,
  homes,
  onConfirm,
  onSchedule,
}: AdoptFromFosterModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!device) {
    return null;
  }

  const { holderName, holderFirstName, id, petName, chip, holderEmail, holderPhone } = device;
  const petLabel = petName || 'this pet';
  // The notice talks about the adopter by first name; fall back to the full name when the
  // transfer only gave us one string to split.
  const adopterFirstName = holderFirstName || holderName;
  // This flow recalls without `saveToRoster`, so the foster household outlives the adoption only
  // when it was already saved — the backend keeps a persisted home and tears down every other one.
  const keepsHome = isFosterOnRoster(device, homes);

  function handleClose() {
    setScheduling(false);
    setDate('');
    setTime('');
    setSubmitError(null);
    onClose();
  }

  const scheduleError = scheduleValidationError(date, time);
  const when = combineDateTime(date, time);
  const canConfirmSchedule = !!when && !scheduleError && !submitting;

  async function handleConfirmSchedule() {
    if (!device || !canConfirmSchedule || !when || !onSchedule) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSchedule(device, when);
      handleClose();
    } catch (err) {
      // Kept open on failure so the rescue can adjust the time.
      setSubmitError(err instanceof Error ? err.message : 'Could not schedule the adoption.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm() {
    // `device` is non-null here (guarded above) but re-checked to satisfy the
    // async closure. Guard against a double-submit while the transfer is in
    // flight — the modal stays open until it resolves, then closes.
    if (!device || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(device);
    } finally {
      setSubmitting(false);
      onClose();
    }
  }

  if (scheduling) {
    return (
      <Modal open={open} onClose={submitting ? () => {} : handleClose} dismissOnBackdropClick={false}>
        <ModalTitle>Schedule foster-fail adoption</ModalTitle>
        <ScheduleStep
          actionLabel="The adoption"
          date={date}
          time={time}
          onDateChange={setDate}
          onTimeChange={setTime}
          error={scheduleError ?? submitError}
        >
          <ModalSubtitle>
            Pick when {petLabel} should be adopted by <strong>{holderName}</strong>. The pet stays in their foster
            placement until then.
          </ModalSubtitle>
        </ScheduleStep>
        <ModalActions>
          <Button variant="ghost" onClick={() => setScheduling(false)} disabled={submitting}>
            Back
          </Button>
          <Button variant="green" onClick={handleConfirmSchedule} disabled={!canConfirmSchedule}>
            {submitting ? 'Scheduling…' : 'Schedule adoption'}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : handleClose}>
      <ModalTitle>Adopt to foster home (Foster Fail)</ModalTitle>
      <ModalSubtitle>
        Adopt {petLabel} to their foster home, with <strong>{holderName}</strong> as the adopter? {holderName} will
        receive an email to set up their own Fi subscription.
        {chip?.id
          ? ` The pet's microchip${chip.serialNumber ? ` (${chip.serialNumber})` : ''} will transfer to the adopter automatically.`
          : ''}
      </ModalSubtitle>

      <dl className={`${styles.confirmDetails} ${styles.confirmDetailsTight}`}>
        <div>
          <dt>Adopter</dt>
          <dd>{holderName}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{holderEmail || '—'}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{holderPhone || '—'}</dd>
        </div>
      </dl>

      <div className={`${styles.notice} ${styles.noticeInfo}`}>
        <NoticeIcon variant="info" />
        <div>
          {petLabel} moves into a household {adopterFirstName} owns outright, so they may need to set up their Safe Zone
          and WiFi again — worth mentioning when you tell them.
          {keepsHome ? ` ${adopterFirstName}'s foster household stays on file, empty, for the next dog.` : ''}
        </div>
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Adopting…' : 'Adopt Now'}
        </Button>
      </ModalActions>
      {onSchedule && (
        <ModalActionsSecondary>
          <Button variant="link" onClick={() => setScheduling(true)} disabled={submitting}>
            Schedule for later
          </Button>
        </ModalActionsSecondary>
      )}
    </Modal>
  );
}
